import extend from "lodash/extend";
import forEach from "lodash/forEach";
import omit from "lodash/omit";
import pick from "lodash/pick";
import uniqueId from "lodash/uniqueId";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Modal } from "PFComponents/modal";
import { useModalContext } from "PFComponents/modal";
import { isProfileFieldPermitted } from "PFCore/helpers/profile_permissions";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { AccessLevel, CurrentProfile } from "PFTypes";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { OnProfileUpdateHandler } from "../../profile_show_page.types";
import { MyInformationFormData } from "./my_information_edit_modal.types";
import { AvatarSection } from "./parts/avatar_section";
import { ContactSection } from "./parts/contact_section";
import { DetailsSection } from "./parts/details_section";

type MyInformationEditModalProps = {
  profile: CurrentProfile;
  onClose: VoidFunction;
  onProfileUpdate: OnProfileUpdateHandler;
};

export const MyInformationEditModal = ({
  profile,
  onProfileUpdate,
  onClose
}: MyInformationEditModalProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts.myInformationModal" });
  const { t: tTranslation } = useTranslation();
  const { data: currentProfile } = useCurrentProfile();

  const formMethods = useForm<MyInformationFormData>({
    defaultValues: {
      firstName: profile.firstName,
      lastName: profile.lastName || "",
      summary: profile.summary || "",
      contactInformation:
        profile.contactInformation.map((contact) => ({
          inputId: uniqueId(),
          ...pick(contact, ["type", "value", "label"])
        })) || []
    }
  });

  const {
    formState: { isDirty },
    handleSubmit,
    setError
  } = formMethods;

  const { showGrowl } = useGrowlStoreActions();
  const { getSingleErrorByType } = useResponseErrors();

  const [isLoading, setLoading] = useState(false);
  const [isConfirmModalDisplayed, setIsConfirmModalDisplayed] = useState(false);

  const isFieldPermitted = (field: string, level: AccessLevel) =>
    isProfileFieldPermitted(currentProfile?.permissionsGroup, profile.permissionsGroup.id, field, level);

  const handleUpdate = ({ firstName, lastName, summary, contactInformation }: MyInformationFormData) => {
    setLoading(true);

    const data = extend(
      {},
      isFieldPermitted("summary", AccessLevel.ReadWrite) && { summary },
      isFieldPermitted("firstAndLastName", AccessLevel.ReadWrite) && {
        firstName,
        lastName
      },
      isFieldPermitted("contact_info", AccessLevel.ReadWrite) && {
        contactInformation: contactInformation.map((contact) => omit(contact, "inputId"))
      }
    );

    return onProfileUpdate(data)
      .then(() => {
        setLoading(false);
        showGrowl({
          message: t("yourInformationUpdated"),
          kind: "success"
        });
      })
      .catch((errorResponse) => {
        const formattedErrors = getSingleErrorByType(errorResponse);
        forEach(formattedErrors, (message, fieldName) => {
          // Parse from "contact_information[0][value]" to "contactInformation.0.value"
          const parsedFieldName = fieldName
            .replace("contact_information", "contactInformation")
            .replace("first_name", "firstName")
            .replace("last_name", "lastName")
            .replaceAll("[", ".")
            .replaceAll("]", "") as keyof MyInformationFormData;
          setError(parsedFieldName, { message });
        });

        setLoading(false);
        throw errorResponse;
      });
  };

  return (
    <Modal
      title={t("myInformation")}
      onClose={onClose}
      onBeforeClose={
        isDirty
          ? () => {
              setIsConfirmModalDisplayed(true);
              return false;
            }
          : undefined
      }
      onOK={handleSubmit(handleUpdate)}
      disableOKButton={!isDirty || isLoading}
      labelOK={tTranslation("save")}
      ariaLabelOK={t("saveMyInformationForm")}
      ariaLabelCancel={t("cancelEditingMyInformation")}
    >
      <FormProvider {...formMethods}>
        <form>
          <DetailsSection isFieldPermitted={isFieldPermitted} />
          <AvatarSection profile={profile} />
          <ContactSection isFieldPermitted={isFieldPermitted} isLoading={isLoading} />
        </form>
      </FormProvider>
      {isConfirmModalDisplayed && (
        <ConfirmCloseModal
          onConfirmClose={() => {
            setIsConfirmModalDisplayed(false);
          }}
        />
      )}
    </Modal>
  );
};

type ConfirmCloseModalProps = {
  onConfirmClose: VoidFunction;
};

const ConfirmCloseModal = ({ onConfirmClose }: ConfirmCloseModalProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts.myInformationModal" });
  const { t: tTranslation } = useTranslation();
  const { closeWithAnimation } = useModalContext();

  return (
    <Modal
      title={t("unsavedChanges")}
      onOK={() => {
        // Close the form modal
        closeWithAnimation(false);
      }}
      onClose={onConfirmClose}
      labelOK={tTranslation("ok")}
    >
      {t("changesNotSaved")}
    </Modal>
  );
};
